import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Form, Spinner, Modal } from 'react-bootstrap';
import { getDatabase, ref, set, get, update } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../../Helpers/DateUtils';

interface LoginPageConfig {
    backgroundImageUrl?: string;
    customerLogoUrl?: string;
    lastUpdatedBy?: string;
    lastUpdatedDate?: string;
}

const LoginPageSettings: React.FC = () => {
    const [config, setConfig] = useState<LoginPageConfig>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'danger', text: string } | null>(null);
    
    const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    // Modal state for removal confirmation
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeType, setRemoveType] = useState<'background' | 'logo' | null>(null);

    const auth = getAuth();
    const database = getDatabase();
    const storage = getStorage();

    // Load current configuration
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const configRef = ref(database, 'publicSettings/loginPage');
                const snapshot = await get(configRef);
                
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setConfig(data);
                    setBackgroundPreview(data.backgroundImageUrl || null);
                    setLogoPreview(data.customerLogoUrl || null);
                }
            } catch (error) {
                console.error('Error loading login page config:', error);
                setMessage({ type: 'danger', text: 'Kunde inte ladda konfiguration' });
            } finally {
                setLoading(false);
            }
        };

        loadConfig();
    }, [database]);

    const handleBackgroundFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'danger', text: 'Vänligen välj en bildfil' });
                return;
            }
            setBackgroundFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setBackgroundPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'danger', text: 'Vänligen välj en bildfil' });
                return;
            }
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadImage = async (file: File, path: string): Promise<string> => {
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, file);
        const downloadUrl = await getDownloadURL(fileRef);
        return downloadUrl;
    };

    const handleRemoveBackground = () => {
        setRemoveType('background');
        setShowRemoveModal(true);
    };

    const handleRemoveLogo = () => {
        setRemoveType('logo');
        setShowRemoveModal(true);
    };

    const confirmRemove = async () => {
        setShowRemoveModal(false);
        setSaving(true);
        setMessage(null);

        try {
            if (removeType === 'background') {
                // Delete from storage if exists
                if (config.backgroundImageUrl) {
                    try {
                        const oldRef = storageRef(storage, 'publicAssets/loginPage/background');
                        await deleteObject(oldRef);
                    } catch (error) {
                        console.log('Old background file not found or already deleted');
                    }
                }

                // Update database - use update with null to remove the field
                const configRef = ref(database, 'publicSettings/loginPage');
                await update(configRef, {
                    backgroundImageUrl: null,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                });

                // Update local state
                const updatedConfig = {
                    ...config,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                };
                delete updatedConfig.backgroundImageUrl;

                setConfig(updatedConfig);
                setBackgroundPreview(null);
                setBackgroundFile(null);
                setMessage({ type: 'success', text: 'Bakgrundsbild borttagen' });
            } else if (removeType === 'logo') {
                // Delete from storage if exists
                if (config.customerLogoUrl) {
                    try {
                        const oldRef = storageRef(storage, 'publicAssets/loginPage/logo');
                        await deleteObject(oldRef);
                    } catch (error) {
                        console.log('Old logo file not found or already deleted');
                    }
                }

                // Update database - use update with null to remove the field
                const configRef = ref(database, 'publicSettings/loginPage');
                await update(configRef, {
                    customerLogoUrl: null,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                });

                // Update local state
                const updatedConfig = {
                    ...config,
                    lastUpdatedBy: auth.currentUser?.uid || '',
                    lastUpdatedDate: formatDateForStorage(new Date())
                };
                delete updatedConfig.customerLogoUrl;

                setConfig(updatedConfig);
                setLogoPreview(null);
                setLogoFile(null);
                setMessage({ type: 'success', text: 'Logotyp borttagen' });
            }
        } catch (error) {
            console.error('Error removing image:', error);
            setMessage({ type: 'danger', text: 'Kunde inte ta bort bilden' });
        } finally {
            setSaving(false);
            setRemoveType(null);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);

        try {
            const updatedConfig: LoginPageConfig = { ...config };

            // Upload background image if selected
            if (backgroundFile) {
                const backgroundUrl = await uploadImage(backgroundFile, 'publicAssets/loginPage/background');
                updatedConfig.backgroundImageUrl = backgroundUrl;
            }

            // Upload logo if selected
            if (logoFile) {
                const logoUrl = await uploadImage(logoFile, 'publicAssets/loginPage/logo');
                updatedConfig.customerLogoUrl = logoUrl;
            }

            // Add metadata
            updatedConfig.lastUpdatedBy = auth.currentUser?.uid || '';
            updatedConfig.lastUpdatedDate = formatDateForStorage(new Date());

            // Save to database
            const configRef = ref(database, 'publicSettings/loginPage');
            await set(configRef, updatedConfig);

            setConfig(updatedConfig);
            setBackgroundFile(null);
            setLogoFile(null);
            setMessage({ type: 'success', text: 'Inställningar sparade!' });
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage({ type: 'danger', text: 'Kunde inte spara inställningar. Kontrollera att du har behörighet.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div className="login-page-settings" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div className="mb-4">
                <h2 style={{ fontSize: '24px', fontWeight: 500, marginBottom: '8px' }}>Inloggningssida</h2>
                <p className="text-muted" style={{ fontSize: '14px', marginBottom: 0 }}>
                    Anpassa bilder som visas på inloggningssidan
                </p>
            </div>

            {message && (
                <Alert 
                    variant={message.type} 
                    dismissible 
                    onClose={() => setMessage(null)}
                    className="mb-4"
                >
                    {message.text}
                </Alert>
            )}

            <div className="mb-4" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                {/* Background Image Card */}
                <Card style={{ border: '1px solid #e0e0e0', borderRadius: '12px', maxWidth: '400px', flex: '1 1 400px' }}>
                    <Card.Body style={{ padding: '24px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 style={{ fontSize: '16px', fontWeight: 500, margin: 0 }}>Bakgrundsbild</h5>
                            {backgroundPreview && (
                                <Button 
                                    variant="link" 
                                    size="sm"
                                    onClick={handleRemoveBackground}
                                    disabled={saving}
                                    style={{ 
                                        color: '#dc3545', 
                                        textDecoration: 'none',
                                        padding: '0',
                                        fontSize: '14px'
                                    }}
                                >
                                    Ta bort
                                </Button>
                            )}
                        </div>

                        {backgroundPreview ? (
                            <div className="mb-3">
                                <img 
                                    src={backgroundPreview} 
                                    alt="Background preview" 
                                    style={{ 
                                        width: '100%', 
                                        height: '200px', 
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0'
                                    }} 
                                />
                            </div>
                        ) : (
                            <div 
                                className="mb-3 d-flex align-items-center justify-content-center"
                                style={{ 
                                    width: '100%', 
                                    height: '200px', 
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '2px dashed #dee2e6',
                                    color: '#6c757d',
                                    fontSize: '14px'
                                }}
                            >
                                Ingen bild vald
                            </div>
                        )}

                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={handleBackgroundFileChange}
                            disabled={saving}
                            size="sm"
                        />
                    </Card.Body>
                </Card>

                {/* Logo Card */}
                <Card style={{ border: '1px solid #e0e0e0', borderRadius: '12px', maxWidth: '400px', flex: '1 1 400px' }}>
                        <Card.Body style={{ padding: '24px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 style={{ fontSize: '16px', fontWeight: 500, margin: 0 }}>Logotyp</h5>
                                {logoPreview && (
                                    <Button 
                                        variant="link" 
                                        size="sm"
                                        onClick={handleRemoveLogo}
                                        disabled={saving}
                                        style={{ 
                                            color: '#dc3545', 
                                            textDecoration: 'none',
                                            padding: '0',
                                            fontSize: '14px'
                                        }}
                                    >
                                        Ta bort
                                    </Button>
                                )}
                            </div>

                            {logoPreview ? (
                                <div className="mb-3">
                                    <div 
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ 
                                            width: '100%', 
                                            height: '200px', 
                                            backgroundColor: '#ffffff',
                                            borderRadius: '8px',
                                            border: '1px solid #e0e0e0',
                                            padding: '20px'
                                        }}
                                    >
                                        <img 
                                            src={logoPreview} 
                                            alt="Logo preview" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: '100%', 
                                                objectFit: 'contain'
                                            }} 
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div 
                                    className="mb-3 d-flex align-items-center justify-content-center"
                                    style={{ 
                                        width: '100%', 
                                        height: '200px', 
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '2px dashed #dee2e6',
                                        color: '#6c757d',
                                        fontSize: '14px'
                                    }}
                                >
                                    Ingen logotyp vald
                                </div>
                            )}

                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleLogoFileChange}
                                disabled={saving}
                                size="sm"
                            />
                    </Card.Body>
                </Card>
            </div>

            <div>
                <Button 
                    variant="primary" 
                    onClick={handleSave}
                    disabled={saving || (!backgroundFile && !logoFile)}
                    style={{ 
                        borderRadius: '8px',
                        padding: '8px 24px'
                    }}
                >
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sparar...
                        </>
                    ) : (
                        'Spara ändringar'
                    )}
                </Button>
            </div>

            {/* Remove Confirmation Modal */}
            <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta borttagning</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {removeType === 'background' 
                        ? 'Är du säker på att du vill ta bort bakgrundsbilden?'
                        : 'Är du säker på att du vill ta bort logotypen?'
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={confirmRemove}>
                        Ta bort
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LoginPageSettings;

